import { MealTagsScrolling } from '@tovala/component-library'
import {
  MealImage,
  MealTag,
  NutritionalInfoItem,
} from '@tovala/browser-apis-combinedapi'
import { ReactNode } from 'react'
import { partition } from 'lodash-es'
import { getDisplayTags } from './utils'

import { TAG_IDS } from 'utils/meals'

import {
  Allergens,
  ChefImage,
  ComponentImages,
  DetailsCollapsible,
  Ingredients,
  MainImage,
  Nutrition,
  PreparationSteps,
  trackDetailsSectionOpened,
} from './shared'

export const MealDetails = ({
  averageRating,
  expirationDate,
  cookTime,
  imageTag,
  meal,
  price,
  reviews,
  stepper,
  title,
}: {
  averageRating?: ReactNode
  expirationDate?: ReactNode
  cookTime?: ReactNode
  imageTag?: ReactNode
  meal: {
    chefImage: string
    createdBy: string
    id: number
    images: MealImage[]
    ingredients: string
    mealPrepSteps: string
    nutritionalInfo: NutritionalInfoItem[]
    story: string
    subtitle: string
    tags: MealTag[]
  }
  price?: ReactNode
  reviews?: ReactNode
  stepper?: ReactNode
  title: ReactNode
}) => {
  const [mainImage, componentImages] = partition(
    meal.images,
    (image) => image.key === 'cell_tile',
  )

  return (
    <>
      <div className="mx-4 my-8">
        <div className="text-k/28_110">{title}</div>
        <p className="text-k/24_120 text-grey-8">{meal.subtitle}</p>
      </div>

      <MainImage
        chefImage={
          meal.chefImage ? (
            <ChefImage chefImage={meal.chefImage} createdBy={meal.createdBy} />
          ) : null
        }
        imageTag={imageTag}
        imageURL={mainImage[0]?.url}
        stepper={stepper}
      />

      {(price || cookTime || averageRating) && (
        // Every item draws a divider on its left. Pulling the row left by one divider's width
        // (margin + border + padding) pushes the divider of whichever item starts a line into
        // the clipped area, so wrapped lines start flush without one. px-1 keeps focus rings visible.
        <div className="mx-3 mt-4 overflow-hidden px-1">
          <div className="-ml-[25px] flex flex-wrap items-center gap-y-2">
            {price && (
              <HeaderItem>
                <p className="text-k/20_110">{price}</p>
              </HeaderItem>
            )}

            {cookTime && <HeaderItem>{cookTime}</HeaderItem>}

            {averageRating && <HeaderItem>{averageRating}</HeaderItem>}
          </div>
        </div>
      )}

      {meal.tags.length > 0 && (
        <div className="my-4 ml-4 overflow-hidden">
          <MealTagsScrolling tags={getDisplayTags({ tags: meal.tags })} />
        </div>
      )}

      <div className="mx-4 mb-32 mt-4">
        <h3 className="mb-4 text-k/20_110 md:mb-3">Description</h3>
        {meal.tags.find((tag) => tag.id === TAG_IDS.FAMILY) && (
          <FamilyMealDisclaimer />
        )}
        <p className="mb-4 text-k/14_120 tracking-normal text-grey-9 md:mb-3">
          {meal.story}
        </p>

        {expirationDate}

        <div className="divide-y divide-grey-4 border-b border-t border-grey-4">
          {meal.nutritionalInfo && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={trackDetailsSectionOpened}
                sectionID="nutrition"
                title="Nutrition"
              >
                <div>
                  <Nutrition nutritionalInfo={meal.nutritionalInfo} />
                </div>
              </DetailsCollapsible>
            </div>
          )}

          {reviews && <div className="empty:hidden">{reviews}</div>}

          {componentImages.length > 0 && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={trackDetailsSectionOpened}
                sectionID="in_the_box"
                title="In the Box"
              >
                <div>
                  <ComponentImages
                    images={componentImages.map(({ caption, url }) => ({
                      caption,
                      url,
                    }))}
                  />
                </div>
              </DetailsCollapsible>
            </div>
          )}

          {meal.ingredients && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={trackDetailsSectionOpened}
                sectionID="ingredients"
                title="Ingredients"
              >
                <Ingredients ingredients={meal.ingredients} />
              </DetailsCollapsible>
            </div>
          )}

          <div>
            <DetailsCollapsible
              onCollapsibleClick={trackDetailsSectionOpened}
              sectionID="allergens"
              title="Allergens"
            >
              <Allergens
                allergens={
                  meal.nutritionalInfo
                    ?.filter(({ key }) => key === 'allergen')
                    .map((allergen) => allergen.name) || []
                }
              />
            </DetailsCollapsible>
          </div>

          {meal.mealPrepSteps && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={trackDetailsSectionOpened}
                sectionID="prep_steps"
                title="Preparation Steps"
              >
                <PreparationSteps steps={splitPrepSteps(meal.mealPrepSteps)} />
              </DetailsCollapsible>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const HeaderItem = ({ children }: { children: ReactNode }) => {
  return (
    <div className="ml-3 flex h-8 items-center border-l border-grey-4 pl-3 empty:hidden">
      {children}
    </div>
  )
}

const FamilyMealDisclaimer = () => {
  return (
    <p className="mb-4 text-k/14_120 tracking-normal text-grey-9">
      <span className="font-semibold">Family Meal:</span> Serves 4. All servings
      cook together, no single servings. Uses your air-fry basket and black
      sheet tray (included with your oven). Nutrition shown per serving.{' '}
      <span className="font-semibold">Questions about Family Meals? </span>
      <a
        className="underline"
        href="https://www.tovala.com/familymealsFAQ"
        rel="noreferrer noopener"
        target="_blank"
      >
        Learn more
      </a>
    </p>
  )
}

function splitPrepSteps(steps: string) {
  return steps.split(/[0-9]+\./g).filter((s) => s)
}
